import { MoreHorizontal } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover";
import { cn } from "../../lib/utils";

export type MoreMenuItem = {
  label: ReactNode;
  icon?: ReactNode;
  /** подпись под пунктом — когда одного названия мало, чтобы выбрать */
  hint?: ReactNode;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
};

/**
 * «⋯» и короткий список действий. Сюда уходит второстепенное и опасное,
 * чтобы рядом с главной кнопкой не стояло трёх равных.
 */
export function MoreMenu({ items, title = "Ещё", className, trigger, align = "end" }: {
  items: (MoreMenuItem | false | null | undefined)[];
  title?: string;
  className?: string;
  /**
   * Своя кнопка вместо «⋯» — например, главная «Опубликовать» с выбором
   * способа. Обычный <button>: в React 18 ref до функционального Button не дойдёт.
   */
  trigger?: ReactNode;
  align?: "start" | "center" | "end";
}) {
  const [open, setOpen] = useState(false);
  const list = items.filter(Boolean) as MoreMenuItem[];
  if (list.length === 0) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            title={title}
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            className={cn("grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground", className)}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      </PopoverTrigger>
      {/* montage-root: портал в body — палитру раздела несём с собой */}
      <PopoverContent className="montage-root w-auto min-w-[220px] max-w-[320px] rounded-[18px] p-1.5" align={align} onClick={(e) => e.stopPropagation()}>
        <div role="menu" className="flex flex-col">
          {list.map((it, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
              className={cn(
                "flex items-start gap-2.5 rounded-[12px] px-3 py-2 text-left text-[13px] font-medium hover:bg-muted disabled:pointer-events-none disabled:opacity-45 [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0",
                it.destructive && "text-rework-fg",
              )}
            >
              {it.icon}
              <span className="flex min-w-0 flex-col gap-0.5">
                {it.label}
                {it.hint && <span className="text-xs font-normal text-muted-foreground">{it.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
